import { Typography } from '@euler/app/components/typography';
import { VerticalLine } from '@euler/app/tv/components/VerticalLine';
import { MaybeText } from '@euler/components/MaybeText';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { getDefectiveLevelOfItemList } from '@euler/functions/getDefectiveLevelOfItemList';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { InventoryQuery } from '@euler/functions/useInventory';
import {
  DefectiveLevel,
  kFrontRecordsSiteCode,
  kRearRecordsSiteCode,
} from '@euler/model';
import { VehicleReport } from '@euler/model/report';
import { arr2groupmap } from '@euler/utils/array';
import { ReactNode, memo } from 'react';
import { StyleProp, TextStyle, View, ViewStyle } from 'react-native';

export const PreInspectionSummaryView = memo(
  ({
    report,
    inventoryQuery,
    style,
    infoCellStyle,
    infoValueStyle,
    infoLabelStyle,
    separatorLineColor = ['rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0)'],
  }: {
    report: VehicleReport;
    inventoryQuery: InventoryQuery;
    style?: StyleProp<ViewStyle>;
    infoCellStyle?: StyleProp<ViewStyle>;
    infoValueStyle?: StyleProp<TextStyle>;
    infoLabelStyle?: StyleProp<TextStyle>;
    separatorLineColor?: string | [string, string];
  }) => {
    const { rpx } = useResponsiveContext();

    let totalCount = 0;
    let defectiveCount = 0;
    let frontRecordCount = 0;
    let rearRecordCount = 0;

    const separatorLineColorStops =
      typeof separatorLineColor === 'string'
        ? [separatorLineColor, separatorLineColor]
        : separatorLineColor;

    const groupMap = arr2groupmap(
      report.preInspection?.items ?? [],
      x => x.siteId,
    );

    for (const key of groupMap.keys()) {
      const items = groupMap.get(key)!;
      const defectiveLevel = getDefectiveLevelOfItemList(items);
      totalCount++;
      if (defectiveLevel !== DefectiveLevel.Fine) {
        defectiveCount++;
      }
      if (key != null) {
        const site = inventoryQuery.getSiteById(key);
        if (site != null) {
          if (site.code === kFrontRecordsSiteCode) {
            frontRecordCount++;
          } else if (site.code === kRearRecordsSiteCode) {
            rearRecordCount++;
          }
        }
      }
    }

    return (
      <HStack
        css={`
          flex-direction: row;
          justify-content: space-between;
          align-items: stretch;
        `}
        style={style}
      >
        <View
          css={`
            flex: 1;
            justify-content: center;
          `}
        >
          <InfoCell
            label="全部检测"
            text={`${totalCount}`}
            labelStyle={infoLabelStyle}
            valueStyle={infoValueStyle}
            style={infoCellStyle}
          />
          <InfoCell
            label="前部记录"
            text={`${frontRecordCount}`}
            labelStyle={infoLabelStyle}
            valueStyle={infoValueStyle}
            css={`
              margin-bottom: 0;
            `}
            style={infoCellStyle}
          />
        </View>
        <VerticalLine
          startColor={separatorLineColorStops[0]}
          endColor={separatorLineColorStops[1]}
          css={`
            margin: ${rpx(4)}px 0;
          `}
        />
        <View
          css={`
            flex: 1;
            justify-content: center;
          `}
        >
          <InfoCell
            label="外观异常"
            text={`${defectiveCount}`}
            labelStyle={infoLabelStyle}
            valueStyle={infoValueStyle}
            style={infoCellStyle}
          />

          <InfoCell
            label="后部记录"
            text={`${rearRecordCount}`}
            labelStyle={infoLabelStyle}
            valueStyle={infoValueStyle}
            css={`
              margin-bottom: 0;
            `}
            style={infoCellStyle}
          />
        </View>
      </HStack>
    );
  },
);

const InfoCell = memo(
  ({
    text,
    label,
    style,
    labelStyle,
    valueStyle,
  }: {
    text: ReactNode | string;
    label: string;
    style?: StyleProp<ViewStyle>;
    valueStyle?: StyleProp<TextStyle>;
    labelStyle?: StyleProp<TextStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          align-items: center;
          margin-bottom: ${rpx(32)}px;
        `}
        style={[style]}
      >
        <MaybeText
          text={text}
          numberOfLines={1}
          css={`
            color: #555;
            font-size: ${rpx(64)}px;
            font-family: ${FontFamily.NotoSans.Bold};
            margin-bottom: ${rpx(8)}px;
          `}
          style={valueStyle}
        />
        <Typography.Label color={'#777'} size={rpx(18)} style={labelStyle}>
          {label}
        </Typography.Label>
      </View>
    );
  },
);
